import {message} from 'antd';

import {BillingService, isProd} from '../../services';
import {brandConfig} from "../../libs/ui/src";
import {AppDispatch} from "../index";

export const SET_CHARGEBEE_INSTANCE = 'SET_CHARGEBEE_INSTANCE';

export const chargebeeActions = {
  setChargebeeInstance,
  getPortalSession,
  activateRegularPlan,
  activateRegularPlanGx,
  activatePlanFromTrial,
};

function setChargebeeInstance(client) {
  return (dispatch: AppDispatch) => {
    let site = isProd() ? 'growth-x' : 'growth-x-test';
    if (brandConfig.isLeadoku || client?.whitelabel === 'leadoku') {
      site = isProd() ? 'leadoku' : 'leadoku-test';
    }
    window.Chargebee.tearDown();
    const instance = window.Chargebee.init({
      site,
      enableGTMTracking: true,
      publishableKey: process.env.NX_PUBLIC_CHARGEBEE_KEYS_PUBLISHABLE,
    });
    dispatch(set(instance));
    return instance;
  };

  function set(instance) {
    return {type: SET_CHARGEBEE_INSTANCE, instance};
  }
}

const PORTAL_SESSION_REQUIRED_FIELDS = ['id', 'token', 'access_url', 'status'];

function parsePortalSession(data: any): any | null {
  const parsed = typeof data === 'string' ? JSON.parse(data) : data;
  // Support both flat response and nested { portal_session: {...} } shape
  const session = parsed?.portal_session ?? parsed;
  const isValid = PORTAL_SESSION_REQUIRED_FIELDS.every(field => field in session);
  return isValid ? session : null;
}

function getPortalSession() {
  return async () => {
    try {
      const response = await BillingService.getPortalSession();
      return parsePortalSession(response.data);
    } catch (_) {
      return null;
    }
  };
}

function activateRegularPlan(updateLoading, period) {
  return async () => {
    const isUpdatingState = updateLoading && typeof updateLoading === 'function';
    try {
      isUpdatingState && updateLoading(true);
      const cbInstance = (window as any).Chargebee.getInstance();
      const response = await BillingService.activateRegularPlan(period);
      cbInstance.openCheckout({
        hostedPage: () => {
          return new Promise(resolve => resolve(response.data));
        },
        success() {
          // TODO refactor GA events
          try {
            (window as any).dataLayer?.push({event: 'success_payment'});
          } catch (e) {
            console.error(e);
          }
        },
      });
    } catch (e) {
      message.error("Sorry, something goes wrong, can't activate regular plan.");
    } finally {
      isUpdatingState && updateLoading(false);
    }
  };
}

function activateRegularPlanGx(updateLoading, plan_id) {
  return async () => {
    const isUpdatingState = updateLoading && typeof updateLoading === 'function';
    try {
      isUpdatingState && updateLoading(true);
      const cbInstance = (window as any).Chargebee.getInstance();
      const response = await BillingService.activateRegularPlanGx(plan_id);
      cbInstance.openCheckout({
        hostedPage: () => {
          return new Promise(resolve => resolve(response.data));
        },
        success() {
          // TODO refactor GA events
          try {
            (window as any).dataLayer?.push({event: 'success_payment'});
          } catch (e) {
            console.error(e);
          }
        },
      });
    } catch (e) {
      message.error("Sorry, something goes wrong, can't activate regular plan.");
    } finally {
      isUpdatingState && updateLoading(false);
    }
  };
}

function activatePlanFromTrial(updateLoading, plan_id, client) {
  return async () => {
    const isUpdatingState = updateLoading && typeof updateLoading === 'function';
    try {
      isUpdatingState && updateLoading(true);
      const cbInstance = (window as any).Chargebee.getInstance();
      cbInstance.setCheckoutCallbacks(() => {
        return {
          success: () => {
            // TODO refactor GA events
            try {
              (window as any).dataLayer?.push({event: 'success_payment'});
            } catch (e) {
              console.error(e);
            }
          },
        };
      });
      const cart = cbInstance.getCart();
      const product = cbInstance.initializeProduct(plan_id);
      cart.setCustomer({
        first_name: client.name,
        email: encodeURIComponent(client.email),
      });
      cart.replaceProduct(product);
      cart.proceedToCheckout();
    } catch (e) {
      message.error("Sorry, something goes wrong, can't activate selected plan.");
    } finally {
      isUpdatingState && updateLoading(false);
    }
  };
}
